import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { PlatformVersions } from "~/common/article/platforms";
import { api, type Block, type PageDetail } from "~/core/api/index";
import { pick, useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { SearchSelect } from "~/core/ui/search-select";
import { Async } from "~/core/ui/states";
import { SmartDistribution } from "~/modules/analytics/features/platform-publishing/smart-distribution";

/** The full page and the article panel share the same material-to-plan workspace. */
export function IntelligentDistribution({
	entityId,
	pageId = "",
	onPageChange,
	data,
	draft,
	sourceDirty,
	initialPlatform,
	initialFormat,
	taskId,
	onEditMaterial,
}: {
	entityId: string;
	pageId?: string;
	onPageChange?: (id: string) => void;
	data?: PageDetail;
	draft?: { title: string; description: string; blocks: Block[] };
	sourceDirty?: boolean;
	initialPlatform?: string;
	initialFormat?: string;
	taskId?: string;
	onEditMaterial?: () => void;
}) {
	const t = useT();
	const { locale } = useI18n();
	const pages = useQuery({
		queryKey: [entityId, "distribution-materials"],
		queryFn: async () =>
			(await Promise.all([api.pages(entityId, { locale: "en" }), api.pages(entityId, { locale: "zh" })])).flat(),
		enabled: !!onPageChange,
	});
	const source = useQuery({
		queryKey: [...keys.entity(entityId), "page", pageId],
		queryFn: () => api.page(pageId),
		enabled: !!pageId && !data,
		...read,
	});
	const material = data ?? source.data;
	const [planOpen, setPlanOpen] = useState(false);
	const [detail, setDetail] = useState(!!initialPlatform);
	const selector = onPageChange && (
		<div className="smart-source-picker">
			{t("smartDistribution.material")}
			<SearchSelect
				value={pageId}
				onChange={onPageChange}
				options={(pages.data ?? []).map((p) => ({ value: p.id, label: p.title }))}
				label={t("smartDistribution.material")}
				placeholder={pick(locale, ["Choose source material", "选择文章素材"])}
				searchLabel={t("table.search")}
				emptyLabel={t("table.noMatch")}
			/>
		</div>
	);
	return (
		<div className="intelligent-distribution">
			{!pageId ? (
				<section className="smart-distribution smart-empty">
					<h3>{t("smartDistribution.heading")}</h3>
					<p>
						{pick(locale, [
							"Choose material, then find the right channels and prepare their content.",
							"选择文章素材，再推荐合适的渠道、生成内容并安排分发。",
						])}
					</p>
					{selector}
					{pages.error && <p role="alert">{t("smartDistribution.error")}</p>}
				</section>
			) : (
				<>
					{sourceDirty && (
						<p className="banner banner-warn">
							{pick(locale, [
								"Save your article changes before recommending channels. Distribution uses the saved material.",
								"请先保存文章修改。智能分发使用已保存的素材。",
							])}
						</p>
					)}
					<div hidden={detail}>
						<SmartDistribution
							key={`${entityId}:${pageId}`}
							entityId={entityId}
							pageId={pageId}
							sourceMaterial={material?.page}
							sourceControl={selector}
							disabledRecommendation={sourceDirty}
							onEditMaterial={onEditMaterial}
							onPlanChange={setPlanOpen}
						/>
					</div>
					{material && !planOpen ? (
						<PlatformVersions
							key={`${entityId}:${pageId}:${initialPlatform ?? ""}:${initialFormat ?? ""}`}
							brandId={entityId}
							pageId={pageId}
							data={material}
							draft={draft ?? material.page}
							initialPlatform={initialPlatform}
							initialFormat={initialFormat}
							taskId={taskId}
							onDetailChange={setDetail}
						/>
					) : !material ? (
						<Async state={source}>{() => null}</Async>
					) : null}
				</>
			)}
		</div>
	);
}
